import { marketingAction } from '../../../_actions/marketing.actions';

/**
 * Service to fetch and transform legacy marketing API data
 * into the Marketing v2 data structure
 */
export const marketingApiService = {
  /**
   * Fetch all marketing automation settings from the API
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise} Transformed automations data
   */
  async getAutomations(dispatch) {
    try {
      const response = await dispatch(marketingAction.getSettings());
      console.log('Marketing API response:', response);

      if (response.data.statusCode === 1 && response?.data?.responseData?.result) {
        const data = response.data.responseData.result;
        console.log('Raw API data:', data);
        const transformed = transformApiToAutomations(data);
        console.log('Transformed automations:', transformed);
        return transformed;
      }

      console.warn('API returned no data or error status');
      return null;
    } catch (error) {
      console.error('Error fetching marketing automations:', error);
      throw error;
    }
  },

  /**
   * Update automation settings
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Update parameters
   * @returns {Promise} API response
   */
  async updateAutomation(dispatch, params) {
    try {
      return await dispatch(marketingAction.updateSetting(params));
    } catch (error) {
      console.error('Error updating automation:', error);
      throw error;
    }
  },

  /**
   * Add a new reminder
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Reminder parameters
   * @returns {Promise} API response
   */
  async addReminder(dispatch, params) {
    try {
      return await dispatch(marketingAction.addReminder(params));
    } catch (error) {
      console.error('Error adding reminder:', error);
      throw error;
    }
  },

  /**
   * Remove a reminder
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Reminder parameters
   * @returns {Promise} API response
   */
  async removeReminder(dispatch, params) {
    try {
      return await dispatch(marketingAction.removeReminder(params));
    } catch (error) {
      console.error('Error removing reminder:', error);
      throw error;
    }
  },

  // ==================== CAMPAIGN METHODS ====================

  /**
   * Create a new campaign (draft or scheduled)
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Campaign parameters
   * @returns {Promise} Campaign data
   */
  async createCampaign(dispatch, params) {
    try {
      const response = await dispatch(marketingAction.createCampaign(params));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result;
      }
      throw new Error('Failed to create campaign');
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  },

  /**
   * Update an existing campaign
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Campaign parameters with campaignId
   * @returns {Promise} Updated campaign data
   */
  async updateCampaign(dispatch, params) {
    try {
      const response = await dispatch(marketingAction.updateCampaign(params));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result;
      }
      throw new Error('Failed to update campaign');
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  },

  /**
   * Send or schedule a campaign
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Send parameters
   * @returns {Promise} Send result with recipient count and cost
   */
  async sendCampaign(dispatch, params) {
    try {
      const response = await dispatch(marketingAction.sendCampaign(params));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result;
      }
      // Surface the backend's reason (e.g. "no recipients...") rather than a
      // generic failure, so the salon knows why nothing sent.
      throw new Error(response?.data?.error?.responseMessage || 'Failed to send campaign');
    } catch (error) {
      console.error('Error sending campaign:', error);
      throw error;
    }
  },

  /**
   * Get list of campaigns with filters
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Filter parameters (currentPage, limit, status, type, search)
   * @returns {Promise} Transformed campaigns array
   */
  async getCampaigns(dispatch, params = {}) {
    try {
      const response = await dispatch(marketingAction.getCampaigns(params));
      if (response.data.statusCode === 1) {
        const campaigns = response.data.responseData.result;
        return transformCampaignsFromApi(campaigns);
      }
      return [];
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  },

  /**
   * Paginated campaign list for the hub — rows plus the metadata the hub
   * needs to page and to keep its tabs/KPIs accurate: per-status counts,
   * hasMore, and (email) all-time sent totals.
   */
  async getCampaignsPaged(dispatch, params = {}) {
    try {
      const response = await dispatch(marketingAction.getCampaigns(params));
      if (response.data.statusCode === 1) {
        const data = response.data.responseData || {};
        return {
          campaigns: transformCampaignsFromApi(data.result || []),
          totalCount: data.totalCount || 0,
          statusCounts: data.statusCounts || {},
          hasMore: !!data.hasMore,
          totals: data.totals || null,
        };
      }
      return { campaigns: [], totalCount: 0, statusCounts: {}, hasMore: false, totals: null };
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  },

  /**
   * Get campaign details with stats
   * @param {Function} dispatch - Redux dispatch function
   * @param {String} campaignId - Campaign ID
   * @returns {Promise} Campaign details with stats
   */
  async getCampaignDetails(dispatch, campaignId) {
    try {
      const response = await dispatch(marketingAction.getCampaignDetails({ campaignId }));
      if (response.data.statusCode === 1) {
        const data = response.data.responseData;
        // The details endpoint returns { campaign, stats } (no .result wrapper).
        // Flatten for the detail panes, dropping the campaign doc's stored
        // bookingCount stub so stats.bookings (live click-basis attribution)
        // wins the `bookingCount ?? bookings` fallback chain.
        if (data && (data.campaign || data.stats)) {
          const merged = { ...(data.campaign || {}), ...(data.stats || {}) };
          delete merged.bookingCount;
          if (Array.isArray(data.recipients)) {
            merged.recipients = data.recipients;
            merged.recipientsTotal = data.recipientsTotal;
            merged.recipientsHasMore = data.recipientsHasMore;
          }
          return merged;
        }
        return data?.result ?? null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      throw error;
    }
  },

  /**
   * Paginated recipient activity for a campaign (20 a page), with an optional
   * activity filter: 'all' | 'opened' | 'clicked' | 'booked'.
   */
  async getCampaignRecipients(dispatch, { campaignId, page = 1, limit = 20, activity = 'all' } = {}) {
    try {
      const response = await dispatch(marketingAction.getCampaignRecipients({ campaignId, page, limit, activity }));
      if (response.data.statusCode === 1) {
        const data = response.data.responseData || {};
        return {
          recipients: Array.isArray(data.recipients) ? data.recipients : [],
          totalCount: data.totalCount || 0,
          hasMore: !!data.hasMore,
          page: data.page || page,
        };
      }
      return { recipients: [], totalCount: 0, hasMore: false, page };
    } catch (error) {
      console.error('Error fetching campaign recipients:', error);
      throw error;
    }
  },

  /**
   * Delete a campaign
   * @param {Function} dispatch - Redux dispatch function
   * @param {String} campaignId - Campaign ID
   * @returns {Promise} Success response
   */
  async deleteCampaign(dispatch, campaignId) {
    try {
      const response = await dispatch(marketingAction.deleteCampaign({ campaignId }));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result;
      }
      throw new Error('Failed to delete campaign');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  },

  /**
   * Estimate cost for a campaign based on audience
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Audience parameters
   * @returns {Promise} Cost estimate
   */
  async estimateCost(dispatch, params) {
    try {
      const response = await dispatch(marketingAction.estimateCost(params));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result;
      }
      return null;
    } catch (error) {
      console.error('Error estimating cost:', error);
      throw error;
    }
  },

  /**
   * Get audience segments with real client counts
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise} Audience segments array with counts
   */
  async getAudienceSegments(dispatch) {
    try {
      const response = await dispatch(marketingAction.getAudienceSegments());
      if (response.data.statusCode === 1 && response.data.responseData) {
        return response.data.responseData.segments || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching audience segments:', error);
      return [];
    }
  },

  // ==================== ANALYTICS METHODS ====================

  /**
   * Get analytics overview data
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Date range parameters
   * @returns {Promise} Analytics overview data
   */
  /**
   * Automation send stats for the Performance page (analytics/automations):
   * { automations: [{ type, name, emailSent, smsSent, opened, clicked,
   * conversions, journeyId? }] }. type is the automation (reminder,
   * bookingUpdate, reviewBooster, ...) or 'journey'. Opens, clicks and
   * conversions aren't tracked on automated sends and come back null.
   * Null if the call fails.
   */
  /**
   * Marketing messages a single client has received, for the client panel's
   * Messages tab. Expected: { communications: [{ id, channel: 'email'|'sms',
   * name, subject, sentAt, status, source }] }. Null until the backend
   * implements analytics/client-communications.
   */
  /**
   * The REAL email template for a fixed automation, rendered by the backend
   * exactly as it sends it. Expected: { html, subject? }. The automation
   * preview panels fall back to the legacy static files (stale since 2024)
   * until this responds.
   */
  async getEmailTemplatePreview(dispatch, params = {}) {
    try {
      const response = await dispatch(marketingAction.getEmailTemplatePreview(params));
      if (response.data.statusCode === 1 && response.data.responseData) {
        return response.data.responseData.result || response.data.responseData;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async getClientCommunications(dispatch, params = {}) {
    try {
      const response = await dispatch(marketingAction.getClientCommunications(params));
      if (response.data.statusCode === 1 && response.data.responseData) {
        return response.data.responseData.result || response.data.responseData;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async getAutomationStats(dispatch, params = {}) {
    try {
      const response = await dispatch(marketingAction.getAutomationStats(params));
      if (response.data.statusCode === 1 && response.data.responseData) {
        return response.data.responseData.result || response.data.responseData;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  async getAnalyticsOverview(dispatch, params = {}) {
    try {
      const response = await dispatch(marketingAction.getAnalyticsOverview(params));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result;
      }
      return null;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  },

  /**
   * Get AI-generated marketing insights
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise} Transformed insights data
   */
  async getInsights(dispatch) {
    try {
      const response = await dispatch(marketingAction.getInsights());
      if (response.data.statusCode === 1) {
        return transformInsights(response.data.responseData.result);
      }
      return null;
    } catch (error) {
      console.error('Error fetching insights:', error);
      throw error;
    }
  },

  /**
   * Get salon reviews for content picker
   * @param {Function} dispatch - Redux dispatch function
   * @returns {Promise} Reviews array
   */
  async getSalonReviews(dispatch) {
    try {
      const response = await dispatch(marketingAction.getSalonReviews());
      if (response.data.statusCode === 1 && response.data.responseData) {
        return response.data.responseData.reviews || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching salon reviews:', error);
      return [];
    }
  },

  /**
   * Export analytics report
   * @param {Function} dispatch - Redux dispatch function
   * @param {Object} params - Export parameters (startDate, endDate, format)
   * @returns {Promise} Download URL
   */
  async exportAnalytics(dispatch, params) {
    try {
      const response = await dispatch(marketingAction.exportAnalytics(params));
      if (response.data.statusCode === 1) {
        return response.data.responseData.result.downloadUrl;
      }
      return null;
    } catch (error) {
      console.error('Error exporting analytics:', error);
      throw error;
    }
  }
};

/**
 * Transform API response data to Marketing v2 automation structure
 * @param {Object} apiData - Raw API response
 * @returns {Object} Transformed automations data
 */
function transformApiToAutomations(apiData) {
  const automations = {
    customAutomations: [],
    reminders: [],
    updates: [],
    sales: [],
    reviewBooster: null
  };

  // Transform custom reminders
  if (apiData.customUpdate && Array.isArray(apiData.customUpdate)) {
    automations.customAutomations = apiData.customUpdate.map((item) => ({
      id: item._id,
      name: item.title || 'Custom Reminder',
      description: item.description || '',
      trigger: 'Booking Reminder',
      triggerTime: item.reminderTime || 0,
      timeframe: getReminderTimeType(item.reminderTimeType),
      emailEnabled: item.statusEmailReminder === 1,
      smsEnabled: item.statusSMSreminder === 1,
      isActive: item.statusEmailReminder === 1 || item.statusSMSreminder === 1,
      type: 'configurable',
      // Email content
      emailSubject: item.emailSubject || '',
      emailBody: item.emailBody || '',
      emailFooter: item.emailFooter || item.footerText || '',
      // SMS content
      smsBody: item.smsBody || item.smsMessage || '',
      _raw: item
    }));
  }

  // Transform regular reminders
  if (apiData.reminders && Array.isArray(apiData.reminders)) {
    automations.reminders = apiData.reminders.map((item) => ({
      id: item._id,
      name: item.title || 'Reminder',
      description: `${item.reminderTime} ${getReminderTimeType(item.reminderTimeType)} before appointment`,
      trigger: 'Booking Reminder',
      triggerTime: item.reminderTime || 0,
      timeframe: getReminderTimeType(item.reminderTimeType),
      emailEnabled: item.statusEmailReminder === 1,
      smsEnabled: item.statusSMSreminder === 1,
      isActive: item.statusEmailReminder === 1 || item.statusSMSreminder === 1,
      type: 'configurable',
      // Email content
      emailSubject: item.emailSubject || '',
      emailBody: item.emailBody || '',
      emailFooter: item.emailFooter || item.footerText || '',
      // SMS content
      smsBody: item.smsBody || item.smsMessage || '',
      _raw: item
    }));
  }

  // Transform booking updates
  if (apiData.bookingUpdate && Array.isArray(apiData.bookingUpdate)) {
    const bookingCategoryData = [
      { id: 1, title: "New booking", note: "Send client details when you book their appointment for them." },
      { id: 2, title: "Updated booking", note: "Send client details if you update their date, time or services." },
      { id: 3, title: "No-show", note: "Let your client know that they missed their appointment." },
      { id: 4, title: "Cancelled booking", note: "Let your client know that you cancelled their appointment." },
      { id: 5, title: "Thank you after visiting", note: "Thank your client for visiting and encourage a rebooking." },
      { id: 6, title: "Thank you for tipping", note: "Let your client know how much their tip means to you." }
    ];

    const bookingUpdateMap = {
      1: 'new-booking',
      2: 'updated-booking',
      3: 'no-show',
      4: 'cancelled-booking',
      5: 'thank-you-visiting',
      6: 'thank-you-tipping'
    };

    automations.updates = apiData.bookingUpdate.map((item) => {
      const categoryData = bookingCategoryData.find(cat => cat.id === item.type) || {};
      return {
        id: bookingUpdateMap[item.type] || item._id,
        name: categoryData.title || '',
        description: categoryData.note || '',
        emailEnabled: item.statusEmailReminder === 1,
        smsEnabled: item.statusSMSreminder === 1,
        isActive: item.statusEmailReminder === 1 || item.statusSMSreminder === 1,
        type: 'simple',
        _raw: item
      };
    });
  }

  // Transform sales updates
  if (apiData.salesUpdate && Array.isArray(apiData.salesUpdate)) {
    const salesCategoryData = [
      { id: 1, title: "Reminder to rebook", note: "Remind your client to rebook a set time after their last booking." },
      { id: 2, title: "Happy birthday", note: "Send your clients a happy birthday message." }
    ];

    const salesUpdateMap = {
      1: 'reminder-to-rebook',
      2: 'happy-birthday'
    };

    automations.sales = apiData.salesUpdate.map((item) => {
      const categoryData = salesCategoryData.find(cat => cat.id === item.type) || {};
      return {
        id: salesUpdateMap[item.type] || item._id,
        name: categoryData.title || '',
        description: categoryData.note || '',
        subtitle: item.reminderTime ? `${item.reminderTime} ${getReminderTimeType(item.reminderTimeType)} after last booking` : '',
        emailEnabled: item.statusEmailReminder === 1,
        smsEnabled: item.statusSMSreminder === 1,
        isActive: item.statusEmailReminder === 1 || item.statusSMSreminder === 1,
        type: 'simple',
        _raw: item
      };
    });
  }

  // Transform review booster
  if (apiData.reviewBooster && Array.isArray(apiData.reviewBooster) && apiData.reviewBooster.length > 0) {
    const reviewItem = apiData.reviewBooster[0];
    automations.reviewBooster = {
      id: reviewItem._id,
      name: 'Google Review Booster',
      description: 'Send a link for Google reviews after every appointment.',
      isActive: reviewItem.statusEmailReminder === 1 || reviewItem.statusSMSreminder === 1,
      googleConnected: true,
      type: 'review',
      emailEnabled: reviewItem.statusEmailReminder === 1,
      smsEnabled: reviewItem.statusSMSreminder === 1,
      _raw: reviewItem
    };
  }

  return automations;
}

/**
 * Convert reminder time type from API (1-5) to readable string
 * @param {Number} type - Time type (1=minutes, 2=hours, 3=days, 4=weeks, 5=months)
 * @returns {String} Readable time type
 */
function getReminderTimeType(type) {
  switch (type) {
    case 1:
      return 'Minutes';
    case 2:
      return 'Hours';
    case 3:
      return 'Days';
    case 4:
      return 'Weeks';
    case 5:
      return 'Months';
    default:
      return 'Days';
  }
}

/**
 * Transform API campaigns to Marketing v2 campaign structure
 * @param {Array} apiData - Raw API campaign data
 * @returns {Array} Transformed campaigns
 */
function transformCampaignsFromApi(apiData) {
  if (!Array.isArray(apiData)) return [];

  return apiData.map(campaign => ({
    id: campaign._id,
    name: campaign.name,
    type: campaign.type,
    status: campaign.status,
    templateId: campaign.templateId,
    audience: campaign.audienceSegment,
    audienceSize: campaign.audienceSize,
    sentDate: campaign.sentDate,
    scheduledDate: campaign.scheduledDate,
    scheduledTime: campaign.scheduledTime,
    subject: campaign.subject,
    // Sender identity (Fresha parity) — inbox display name + reply-to address.
    // Surfaced top-level so the edit form can pre-fill them.
    senderName: campaign.senderName,
    replyToEmail: campaign.replyToEmail,
    message: campaign.message,
    // Set when salon-controlled marketing holds this draft/schedule back.
    heldReason: campaign.heldReason || null,
    // Bailey's plans: which plan made it ('bailey:<planKey>:<channel>'), and
    // a gap-filling text's send-time check (status 'skipped' when the times
    // it offered had filled; skipReason says why).
    source: campaign.source || null,
    // The Studio campaign it's part of (campaignStore id), if any
    groupId: campaign.groupId || null,
    guard: campaign.guard || null,
    skipReason: campaign.skipReason || null,
    skippedAt: campaign.skippedAt || null,
    // bookingCount / revenue on list rows are the live click-basis
    // attribution (14-day window), computed by the backend on read.
    stats: campaign.status === 'sent' ? {
      sent: campaign.totalRecipients || 0,
      delivered: campaign.deliveredCount || 0,
      opened: campaign.openedCount || 0,
      clicked: campaign.clickedCount || 0,
      bookings: campaign.bookingCount || 0,
      revenue: campaign.revenue || 0
    } : null,
    _raw: campaign
  }));
}

/**
 * Transform API insights to Marketing v2 insights structure
 * @param {Object} apiData - Raw API insights data
 * @returns {Object} Transformed insights with scenarios
 */
function transformInsights(apiData) {
  // Only what the server measured: no made-up fallback figures ("342 clients
  // haven't booked", "~£4,100"), which read as this salon's own numbers.
  const d = apiData || {}
  const scenarios = []
  if (d.bestSendTime) {
    scenarios.push({
      id: 'timing',
      type: 'info',
      iconBgColor: '#e0e7ff',
      iconColor: '#4f46e5',
      metric: `${d.bestSendTime.day} ${d.bestSendTime.hour}am has ${d.bestSendTime.openRate}% higher open rates`,
      advice: `Schedule your next campaign for ${d.bestSendTime.day} morning to get the most opens`,
      actionLabel: 'Adjust schedule'
    })
  }
  if (d.inactiveClients) {
    const value = Number(d.inactiveClients.potentialRevenue)
    scenarios.push({
      id: 'segment',
      type: 'warning',
      iconBgColor: '#fef3c7',
      iconColor: '#d97706',
      metric: `${d.inactiveClients.count} clients haven't booked in 60+ days`,
      advice: value > 0
        ? `Launch a win-back campaign to recover ~£${value.toLocaleString()} in potential revenue`
        : 'Launch a win-back campaign to bring them back',
      actionLabel: 'Create win-back'
    })
  }
  if (d.topSegment) {
    scenarios.push({
      id: 'upsell',
      type: 'success',
      iconBgColor: '#d1fae5',
      iconColor: '#059669',
      metric: `${d.topSegment.segment} clients book ${d.topSegment.avgBookings}x more often`,
      advice: 'Target this segment first for your next promotional campaign',
      actionLabel: 'Target segment'
    })
  }
  return {
    projection: d.estimatedRevenue ? { nextCampaignRevenue: d.estimatedRevenue, percentAboveAverage: d.percentAboveAverage || null } : null,
    scenarios
  }
}
